import ImageUploaderModal from "../imageUploader/upload/ImageUploaderModal/ImageUploaderModal";
import BasePreview from "../../forms/basePreview/basePreview";
import AddImage from "images/add-image-icon.svg?react";
import AddImageCombined from "images/add-image-combined-icon.svg?react";
import ImageAspectRatio from "enums/imageAspectRatio";
import ValidatedField from "../../forms/ValidatedField";
import cn from "classnames";
import type { AnySchema } from "yup";
import { type ReactNode, useState } from "react";
import { type FormikErrors } from "formik/dist/types";

import "./imagePreview.scss";

export interface ImagePreviewProps {
  label?: string;
  value?: string | null;
  originalValue?: string;
  className?: string;
  disabled?: boolean;
  propertyName: string;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<unknown>>;
  onImageChanged?: (propertyName: string, imageName: string) => void;
  placeholder?: ReactNode;
  endpointToGetSasLink: string;
  editBtnEnabled?: boolean;
  getImageCustomValidator?: () => { validator: AnySchema; propName: string };
  removeBtnEnabled?: boolean;
  shouldValidate?: boolean;
  aspectRatio?: ImageAspectRatio;
  markAsRequired?: boolean;
  isHidden?: boolean;
  headerPlaceholder?: string;
  showBestCrop?: boolean;
  tooltip?: { info: React.ReactNode; width: number; hoverable?: boolean };
  editable?: boolean;
  isReadOnlyView?: boolean;
  readOnlyEmptyState?: string;
  [key: string]: unknown;
}

export default function ImagePreview(props: ImagePreviewProps) {
  const {
    value,
    originalValue,
    className,
    label,
    disabled,
    placeholder = "No Thumbnail",
    editBtnEnabled = true,
    getImageCustomValidator,
    removeBtnEnabled = false,
    endpointToGetSasLink,
    onImageChanged,
    aspectRatio = ImageAspectRatio.HD_16X9,
    isHidden,
    markAsRequired,
    propertyName = "",
    headerPlaceholder,
    combinedIcon = false,
    showBestCrop,
    tooltip,
    editable,
    customImagePreview,
    shouldValidate,
    setFieldValue,
    isReadOnlyView,
    readOnlyEmptyState,
    ...fieldProps
  } = props;

  const [rawImage, setRawImage] = useState<string | null>(null);

  const handleImageChange = async (imageUri: string, imageName: string) => {
    if (!rawImage) {
      setRawImage(imageUri);
    }
    await setFieldValue?.(propertyName, imageUri, shouldValidate);
    onImageChanged && onImageChanged(propertyName, imageName);
  };

  const handleImageLoaded = (image: ArrayBuffer | string | null) => {
    setRawImage(typeof image === "string" ? image : null);
  };

  const imagePlaceholderContent = (
    <div className="placeholder">
      {combinedIcon ? (
        <AddImageCombined className="add-image-combined-icon" />
      ) : (
        <AddImage className="add-image-icon" />
      )}
      <span className="header-placeholder">{headerPlaceholder}</span>
      <span className="text">{placeholder}</span>
    </div>
  );

  const onRemove = async () => {
    await handleImageChange("", "");
  };

  const renderPreview = (onClick: () => void): ReactNode => {
    return (
      <ValidatedField {...fieldProps} propertyName={propertyName}>
        <BasePreview
          value={value}
          className={cn(className, { "custom-image-preview": customImagePreview })}
          label={label}
          disabled={!!disabled}
          isImagePreview
          onClick={onClick}
          onRemove={onRemove}
          placeholder={imagePlaceholderContent}
          aspectRatio={aspectRatio}
          markAsRequired={markAsRequired}
          editBtnEnabled={editBtnEnabled}
          removeBtnEnabled={removeBtnEnabled}
          tooltip={tooltip}
          editable={editable}
        />
      </ValidatedField>
    );
  };

  const imageUploaderKey = `${propertyName}.imageuploader`;

  if (isReadOnlyView) {
    const useEmptyState = value === "";
    if (useEmptyState) {
      return (
        <div className="image-preview-read-only-wrapper">
          <span>{label}</span>
          <div className="image-preview-read-only-value">{readOnlyEmptyState}</div>
        </div>
      );
    }
    return (
      <BasePreview
        value={value}
        className={cn(className, { "custom-image-preview": customImagePreview, "read-only": isReadOnlyView })}
        label={label}
        disabled={!!disabled}
        isImagePreview
        onClick={() => {}}
        onRemove={onRemove}
        placeholder={imagePlaceholderContent}
        aspectRatio={aspectRatio}
        markAsRequired={false}
        editBtnEnabled={editBtnEnabled}
        removeBtnEnabled={removeBtnEnabled}
      />
    );
  }

  return (
    <ImageUploaderModal
      value={rawImage}
      trigger={renderPreview}
      onImageChange={handleImageChange}
      onImageLoaded={handleImageLoaded}
      endpointToGetSasLink={endpointToGetSasLink}
      aspectRatio={aspectRatio}
      isHidden={isHidden}
      key={imageUploaderKey}
      index={imageUploaderKey}
      getImageCustomValidator={getImageCustomValidator}
      showBestCrop={showBestCrop}
    />
  );
}
